// Apolo IDE — client for the remote-dispatch API (/api/dispatch).
//
// Thin wrappers over apiFetch (auto-JWT). Used by useRemoteRunner (the Mac
// runner: poll/claim/postEvents/pollApproval/complete) and by the phone UI
// (createDispatch/sendApproval/requestCancel/listDispatches).

import { apiFetch } from './api.js';

async function unwrap(path, opts) {
  const res = await apiFetch(path, opts);
  if (!res.ok) throw new Error(res.error || `dispatch request failed (${res.status})`);
  return res.data;
}

/** Phone: create a new dispatch. Returns { id, dispatch }. */
export async function createDispatch({ prompt, context, autonomy } = {}) {
  return unwrap('/api/dispatch', {
    method: 'POST',
    body: { prompt, context: context || {}, autonomy: autonomy === 'auto' ? 'auto' : 'ask' },
  });
}

/** Runner: list pending dispatches for the authed user. Returns an array. */
export async function pollPending(signal) {
  const data = await unwrap('/api/dispatch/pending', { method: 'GET', signal });
  return data?.dispatches || [];
}

/** Runner: claim a pending dispatch. Returns { ok, dispatch } or { ok:false } if already claimed. */
export async function claim(id) {
  const res = await apiFetch(`/api/dispatch/${id}/claim`, { method: 'POST' });
  if (res.ok) return { ok: true, dispatch: res.data?.dispatch };
  if (res.status === 409) return { ok: false, reason: 'already_claimed' };
  throw new Error(res.error || `claim failed (${res.status})`);
}

/** Runner: append progress events. `events` = [{ type, payload }]. Returns { ok, seq }. */
export async function postEvents(id, events) {
  return unwrap(`/api/dispatch/${id}/events`, { method: 'POST', body: { events } });
}

/** Runner: poll for the phone's approval decision. Returns { decided, approved? }. */
export async function pollApproval(id, requestId, signal) {
  return unwrap(`/api/dispatch/${id}/approval/${requestId}`, { method: 'GET', signal });
}

/** Phone: approve/reject a pending approval request. */
export async function sendApproval(id, requestId, approved) {
  return unwrap(`/api/dispatch/${id}/approvals`, {
    method: 'POST',
    body: { request_id: requestId, approved: !!approved },
  });
}

/** Runner: report the terminal result. body = { status, result?, error? }. */
export async function complete(id, body) {
  return unwrap(`/api/dispatch/${id}/complete`, { method: 'POST', body });
}

/** Phone: request cancellation. */
export async function requestCancel(id) {
  return unwrap(`/api/dispatch/${id}/cancel`, { method: 'POST' });
}

/** Either side: full replay. Returns { dispatch, events }. */
export async function getDispatch(id) {
  return unwrap(`/api/dispatch/${id}`, { method: 'GET' });
}

/** Phone: dispatch history (newest first). Returns an array. */
export async function listDispatches() {
  const data = await unwrap('/api/dispatch', { method: 'GET' });
  return data?.dispatches || [];
}

/** Phone: what brains the Mac runner reports available. Returns the caps object. */
export async function getCapabilities() {
  const data = await unwrap('/api/dispatch/runner/capabilities', { method: 'GET' });
  return data?.capabilities || {};
}

/** Runner: report available brains (cloud models + ollama/lmstudio online). */
export async function postCapabilities(capabilities) {
  return unwrap('/api/dispatch/runner/capabilities', { method: 'POST', body: capabilities });
}

/** Phone: folders/repos the Mac advertises. Returns an array of { path, label, lastUsed }. */
export async function getRoots() {
  const data = await unwrap('/api/dispatch/runner/roots', { method: 'GET' });
  return data?.roots || [];
}

/** Runner: advertise available folders/repos. */
export async function postRoots(roots) {
  return unwrap('/api/dispatch/runner/roots', { method: 'POST', body: { roots } });
}

/** Phone: is the Mac runner online? Returns { online, lastSeen }. */
export async function getPresence() {
  const data = await unwrap('/api/dispatch/runner/presence', { method: 'GET' });
  return { online: !!data?.online, lastSeen: data?.lastSeen || 0 };
}

/** Runner: heartbeat (keeps presence "online"). */
export async function postHeartbeat() {
  return unwrap('/api/dispatch/runner/heartbeat', { method: 'POST' });
}

/** Phone: register a Web Push subscription for dispatch notifications. */
export async function subscribePush(subscription) {
  return unwrap('/api/dispatch/push/subscribe', { method: 'POST', body: { subscription } });
}
